import { useEffect, useState } from 'react';
import { useList, useCustomMutation, useGo, type CrudFilter, type CrudSort } from '@refinedev/core';
import {
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Loader2,
  Infinity as InfinityIcon,
  Pencil,
  Search,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { endOfDayUtcIso, dateFromUtcDay, toYmd } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable, type Column } from '@/components/tables';
import { notify } from '@/lib/notify';
import {
  MEMBERSHIP_STATUS_LABELS,
  ACCESS_SOURCE_TYPE_LABELS,
  type ChannelMembership,
  type ChannelMembershipStatus,
} from '@/resources/channel-memberships/types';

// Перевод статуса платежа.
const PAYMENT_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Ожидает',
  COMPLETED: 'Оплачен',
  CONFIRMED: 'Оплачен',
  FAILED: 'Ошибка',
  CANCELLED: 'Отменён',
  REFUNDED: 'Возврат',
  PARTIALLY_REFUNDED: 'Частичный возврат',
};

const formatMoney = (amount: string | null | undefined, currency: string | null | undefined) => {
  if (amount === null || amount === undefined) {return '—';}
  const n = Number(amount);
  const value = Number.isFinite(n) ? new Intl.NumberFormat('ru-RU').format(n) : amount;
  return currency ? `${value} ${currency}` : value;
};

interface ChannelPassMembershipsProps {
  /** ID Telegram-канала (channelPass.channelId), а не channelPass.id */
  channelId: number;
}

const PAGE_SIZE = 20;

const STATUS_FILTER_OPTIONS: { value: ChannelMembershipStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'Все статусы' },
  { value: 'ACTIVE', label: MEMBERSHIP_STATUS_LABELS.ACTIVE },
  { value: 'JOIN_REQUEST_SENT', label: MEMBERSHIP_STATUS_LABELS.JOIN_REQUEST_SENT },
  { value: 'PENDING', label: MEMBERSHIP_STATUS_LABELS.PENDING },
  { value: 'LEFT', label: MEMBERSHIP_STATUS_LABELS.LEFT },
  { value: 'KICKED', label: MEMBERSHIP_STATUS_LABELS.KICKED },
  { value: 'EXPIRED', label: MEMBERSHIP_STATUS_LABELS.EXPIRED },
  { value: 'BOT_BLOCKED', label: MEMBERSHIP_STATUS_LABELS.BOT_BLOCKED },
];

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) {return '—';}
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const userLabel = (m: ChannelMembership) => {
  const name = [m.user?.firstName, m.user?.lastName].filter(Boolean).join(' ').trim();
  if (name) {return name;}
  if (m.user?.username) {return `@${m.user.username}`;}
  return `#${m.userId}`;
};

export function ChannelPassMemberships({ channelId }: ChannelPassMembershipsProps) {
  const go = useGo();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState<ChannelMembershipStatus | 'ALL'>('ALL');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [edit, setEdit] = useState<ChannelMembership | null>(null);

  // Debounce поиска: на бэк уходит не чаще раза в 400мс.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Любая смена фильтра/поиска возвращает на первую страницу.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, status]);

  const filters: CrudFilter[] = [{ field: 'channelId', operator: 'eq', value: channelId }];
  if (debouncedSearch) {
    filters.push({ field: 'search', operator: 'eq', value: debouncedSearch });
  }
  if (status !== 'ALL') {
    filters.push({ field: 'status', operator: 'eq', value: status });
  }

  // Сортировка по сроку доступа — серверная (subquery по AccessGrant.endsAt).
  const sorters: CrudSort[] = [{ field: 'accessEndsAt', order: sortOrder }];

  const membershipsQuery = useList<ChannelMembership>({
    resource: 'channel-memberships',
    filters,
    sorters,
    pagination: { currentPage: page, pageSize: PAGE_SIZE, mode: 'server' },
  });

  const memberships = membershipsQuery.result?.data ?? [];
  const total = membershipsQuery.result?.total ?? 0;
  const isLoading = membershipsQuery.query?.isLoading ?? false;
  const refetch = membershipsQuery.query?.refetch;

  const updateMutation = useCustomMutation();

  const getStatusBadge = (membershipStatus: ChannelMembershipStatus) => {
    const label = MEMBERSHIP_STATUS_LABELS[membershipStatus] ?? membershipStatus;
    switch (membershipStatus) {
      case 'ACTIVE':
        return (
          <Badge className="bg-green-500/10 text-green-500 hover:bg-green-500/20">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            {label}
          </Badge>
        );
      case 'JOIN_REQUEST_SENT':
        return (
          <Badge className="bg-yellow-500/10 text-yellow-600 hover:bg-yellow-500/20">
            <Clock className="h-3 w-3 mr-1" />
            {label}
          </Badge>
        );
      case 'KICKED':
      case 'BOT_BLOCKED':
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            {label}
          </Badge>
        );
      case 'PENDING':
        return <Badge variant="outline">{label}</Badge>;
      case 'LEFT':
      case 'EXPIRED':
        return <Badge variant="secondary">{label}</Badge>;
      default:
        return <Badge variant="outline">{label}</Badge>;
    }
  };

  const getDaysRemainingBadge = (daysRemaining: number | null, accessEndsAt: string | null) => {
    if (daysRemaining === null && !accessEndsAt) {
      return (
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <InfinityIcon className="h-3.5 w-3.5" />
          <span className="text-xs">Бессрочно</span>
        </div>
      );
    }
    if (daysRemaining === null) {return '—';}
    if (daysRemaining === 0) {
      return <Badge variant="destructive" className="text-xs">Истёк</Badge>;
    }
    if (daysRemaining <= 3) {
      return <Badge variant="destructive" className="text-xs">{daysRemaining} дн.</Badge>;
    }
    if (daysRemaining <= 7) {
      return <Badge className="bg-orange-500/10 text-orange-600 text-xs">{daysRemaining} дн.</Badge>;
    }
    if (daysRemaining <= 30) {
      return <Badge className="bg-yellow-500/10 text-yellow-600 text-xs">{daysRemaining} дн.</Badge>;
    }
    return <Badge variant="secondary" className="text-xs">{daysRemaining} дн.</Badge>;
  };

  const columns: Column<ChannelMembership>[] = [
    {
      key: 'user',
      header: 'Пользователь',
      render: (_, record) => (
        <div className="flex flex-col">
          <span className="font-medium">{userLabel(record)}</span>
          {record.user?.username && (
            <span className="text-xs text-muted-foreground">@{record.user.username}</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Статус',
      render: (_, record) => (
        <div className="flex items-center gap-2">
          {getStatusBadge(record.status)}
          {record.lastError && (
            <span title={record.lastError} className="text-destructive">
              <AlertTriangle className="h-4 w-4" />
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'daysRemaining',
      header: 'Осталось',
      render: (_, record) => getDaysRemainingBadge(record.daysRemaining, record.accessEndsAt),
    },
    {
      key: 'accessEndsAt',
      header: 'Доступ до',
      render: (_, record) => (
        <span className="text-sm text-muted-foreground">{formatDate(record.accessEndsAt)}</span>
      ),
    },
    {
      key: 'tier',
      header: 'Тариф',
      render: (_, record) =>
        record.tier?.name ? (
          <span className="text-sm">{record.tier.name}</span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      key: 'source',
      header: 'Источник',
      render: (_, record) => (
        <div className="flex flex-col">
          <span className="text-sm">
            {record.accessSourceType
              ? (ACCESS_SOURCE_TYPE_LABELS[record.accessSourceType] ?? record.accessSourceType)
              : '—'}
          </span>
          {record.joinedAt && (
            <span className="text-xs text-muted-foreground">
              вступил {formatDate(record.joinedAt)}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'payment',
      header: 'Платёж',
      render: (_, record) => {
        if (!record.payment) {
          return <span className="text-sm text-muted-foreground">—</span>;
        }
        const statusLabel = record.payment.status
          ? (PAYMENT_STATUS_LABELS[record.payment.status] ?? record.payment.status)
          : null;
        return (
          <div className="flex flex-col">
            <span className="text-sm">
              {formatMoney(record.payment.amount, record.payment.currency)}
            </span>
            {statusLabel && (
              <span className="text-xs text-muted-foreground">{statusLabel}</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      render: (_, record) =>
        record.accessGrantId ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              setEdit(record);
            }}
            title="Изменить срок доступа"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Поиск по имени / username"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={(v) => setStatus(v as ChannelMembershipStatus | 'ALL')}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortOrder} onValueChange={(v) => setSortOrder(v as 'asc' | 'desc')}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="asc">Срок: сначала истекающие</SelectItem>
              <SelectItem value="desc">Срок: сначала длительные</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={memberships}
          total={total}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          onRowClick={(m) => go({ to: `/channel-memberships/show/${m.id}` })}
          emptyMessage="Нет участников канала"
        />
      )}

      <EditAccessDialog
        membership={edit}
        onClose={() => setEdit(null)}
        onSaved={() => {
          setEdit(null);
          refetch?.();
        }}
        mutate={updateMutation.mutate}
      />
    </div>
  );
}

// --- Диалог редактирования срока доступа: режим «дни» или «дата» ---

interface EditAccessDialogProps {
  membership: ChannelMembership | null;
  onClose: () => void;
  onSaved: () => void;
  mutate: ReturnType<typeof useCustomMutation>['mutate'];
}

function EditAccessDialog({ membership, onClose, onSaved, mutate }: EditAccessDialogProps) {
  const [mode, setMode] = useState<'days' | 'date'>('days');
  const [days, setDays] = useState('30');
  const [date, setDate] = useState<Date | null>(null);

  // Реинициализация при открытии нового membership.
  const currentId = membership?.id ?? null;
  const [initFor, setInitFor] = useState<string | null>(null);
  if (membership && initFor !== currentId) {
    setInitFor(currentId);
    setMode('days');
    setDate(membership.accessEndsAt ? dateFromUtcDay(membership.accessEndsAt) : null);
    setDays('30');
  }

  if (!membership) {return null;}

  // Вычисляем итоговую дату окончания: дни → сегодня+N (конец дня UTC), дата → конец дня UTC.
  const resolveEndsAt = (): string | null => {
    if (mode === 'date') {
      return date ? endOfDayUtcIso(date) : null;
    }
    const n = Number(days);
    if (!Number.isFinite(n) || n <= 0) {return null;}
    const d = new Date();
    d.setDate(d.getDate() + Math.floor(n));
    return endOfDayUtcIso(d);
  };

  const handleSave = () => {
    if (!membership.accessGrantId) {return;}
    const endsAt = resolveEndsAt();
    mutate(
      {
        url: `/users/${membership.userId}/access-grants/${membership.accessGrantId}`,
        method: 'patch',
        values: { endsAt },
      },
      {
        onSuccess: () => {
          notify.success('Срок доступа обновлён');
          onSaved();
        },
        onError: () => notify.error('Ошибка при обновлении срока'),
      },
    );
  };

  const preview = (() => {
    const endsAt = resolveEndsAt();
    if (!endsAt) {return 'Бессрочно';}
    return toYmd(new Date(endsAt));
  })();

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Срок доступа — {userLabel(membership)}</DialogTitle>
          <DialogDescription>
            Текущий: {membership.accessEndsAt ? toYmd(new Date(membership.accessEndsAt)) : 'бессрочно'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <Button
              type="button"
              variant={mode === 'days' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMode('days')}
            >
              Кол-во дней
            </Button>
            <Button
              type="button"
              variant={mode === 'date' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMode('date')}
            >
              Дата окончания
            </Button>
          </div>

          {mode === 'days' ? (
            <div className="space-y-2">
              <Label>Дней доступа от сегодня</Label>
              <Input
                type="number"
                min={1}
                value={days}
                onChange={(e) => setDays(e.target.value)}
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Дата окончания (пусто = бессрочно)</Label>
              <DatePicker value={date} onChange={(d) => setDate(d)} />
            </div>
          )}

          <p className="text-sm text-muted-foreground">
            Новый срок: <span className="font-medium text-foreground">{preview}</span>
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={handleSave}>Сохранить</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
